'use client';

import React from 'react';
import { Flame, Trophy } from 'lucide-react';

interface StreakCardProps {
  streak: {
    currentStreak: number;
    longestStreak: number;
    lastActiveDate: string | null;
  };
}

export default function StreakCard({ streak }: StreakCardProps) {
  const today = new Date().toISOString().split('T')[0];
  const isActiveToday = streak.lastActiveDate === today;
  const atRisk = !isActiveToday && streak.currentStreak > 0;

  return (
    <div
      style={{
        backgroundColor: atRisk ? 'rgba(153, 60, 29, 0.08)' : 'var(--bg-card)',
        border: `1px solid ${atRisk ? 'var(--phase5-border)' : 'var(--border)'}`,
        borderRadius: '12px',
        padding: '24px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)', letterSpacing: '0.02em' }}>
          Study Streak
        </span>
        <Flame
          size={18}
          style={{ color: atRisk ? 'var(--phase5-text)' : streak.currentStreak > 0 ? 'var(--accent)' : 'var(--text-muted)' }}
        />
      </div>

      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            fontFamily: 'DM Serif Display, Georgia, serif',
            fontSize: '48px',
            fontWeight: 400,
            color: atRisk ? 'var(--phase5-text)' : 'var(--text-primary)',
            lineHeight: 1,
          }}
        >
          {streak.currentStreak}
        </div>
        <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
          {streak.currentStreak === 1 ? 'day' : 'days'} in a row
        </div>
      </div>

      {atRisk && (
        <div
          style={{
            padding: '8px 10px',
            backgroundColor: 'rgba(153, 60, 29, 0.12)',
            borderRadius: '6px',
            border: '1px solid var(--phase5-border)',
            fontSize: '12px',
            color: 'var(--phase5-text)',
            textAlign: 'center',
          }}
        >
          ⚠ Study today to keep your streak
        </div>
      )}

      {!atRisk && isActiveToday && (
        <div
          style={{
            padding: '8px 10px',
            backgroundColor: 'var(--accent-glow)',
            borderRadius: '6px',
            border: '1px solid var(--accent-border)',
            fontSize: '12px',
            color: 'var(--accent)',
            textAlign: 'center',
          }}
        >
          ✓ Studied today
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 10px',
          backgroundColor: 'var(--bg-elevated)',
          borderRadius: '6px',
          fontSize: '12px',
          color: 'var(--text-muted)',
        }}
      >
        <Trophy size={12} />
        <span>Best: <span style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'var(--text-secondary)' }}>{streak.longestStreak} days</span></span>
      </div>
    </div>
  );
}